import z from 'zod';
import { idArray } from './shared.schema';

export const invoiceRateTypeSchema = z.enum(['Monthly', 'Validation']);

export const feeTypeSchema = z.enum([
	'Access Device',
	'Activation',
	'Beginning Balance',
	'Credit Card',
	'Late',
	'Recurring Late',
	'Lost Device',
	'Mail Invoice',
	'Monthly',
	'Nonrecurring Payment',
	'Transaction',
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Manual Monthly',
	'Miscellaneous',
	'Applied Credit',
	'Carryover',
	'Daily & Hourly',
	'Hourly',
	'Tier',
	'Vehicle Surcharge',
]);

export const invoiceRateTypes = invoiceRateTypeSchema.options;

const filterSchema = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('accountId'),
		value: z.number().int(),
	}),
	z.object({
		type: z.literal('subscriptionName'),
		value: z.string(),
	}),
]);

export const invoiceReportFormatSchema = z.enum(['csv', 'pdf', 'xlsx']);

export const invoiceParametersSchema = z
	.object({
		areaIds: idArray.optional(),
		branchIds: idArray.optional(),
		districtIds: idArray.optional(),
		organizationIds: idArray.optional(),
		facilityIds: z.array(z.string()).optional(),
		accountFilter: filterSchema.optional(),
		invoiceStartDate: z.iso.date().optional(),
		invoiceEndDate: z.iso.date().optional(),
		parkingPeriodStartMonth: z.number().int().min(0).max(11).optional(),
		parkingPeriodStartYear: z.number().int().min(2021).max(2030).optional(),
		parkingPeriodEndMonth: z.number().int().min(0).max(11).optional(),
		parkingPeriodEndYear: z.number().int().min(2021).max(2030).optional(),
		rateType: z.array(invoiceRateTypeSchema).optional(),
		feeType: z.array(feeTypeSchema).optional(),
		subscriberId: z.string().optional(),
		subscriptionName: z.string().optional(),
		payorType: z.string().optional(),
		format: invoiceReportFormatSchema.default('csv'),
		facilityNumber: z.union([z.string(), z.number()]).optional(),
	})
	.superRefine((value, ctx) => {
		const hasInvoiceStartDate = value.invoiceStartDate !== undefined;
		const hasParkingPeriodStart =
			value.parkingPeriodStartMonth !== undefined && value.parkingPeriodStartYear !== undefined;

		if (!hasInvoiceStartDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceStartDate'],
				message: 'Either invoiceStartDate or parking period start month and year is required',
			});
		}

		if (value.invoiceEndDate && !hasInvoiceStartDate) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceEndDate'],
				message: 'Cannot specify invoiceEndDate without invoiceStartDate',
			});
		}

		if (
			(value.parkingPeriodEndMonth !== undefined || value.parkingPeriodEndYear !== undefined) &&
			!hasParkingPeriodStart
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndMonth'],
				message: 'Cannot specify a parking period end without a parking period start',
			});
		}

		if (value.parkingPeriodEndMonth !== undefined && value.parkingPeriodEndYear === undefined) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndYear'],
				message: 'parkingPeriodEndYear is required when parkingPeriodEndMonth is provided',
			});
		}

		if (value.parkingPeriodEndYear !== undefined && value.parkingPeriodEndMonth === undefined) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndMonth'],
				message: 'parkingPeriodEndMonth is required when parkingPeriodEndYear is provided',
			});
		}

		if (
			value.parkingPeriodStartMonth !== undefined &&
			value.parkingPeriodStartYear !== undefined &&
			value.parkingPeriodEndMonth !== undefined &&
			value.parkingPeriodEndYear !== undefined
		) {
			const startValue = value.parkingPeriodStartYear * 12 + value.parkingPeriodStartMonth;
			const endValue = value.parkingPeriodEndYear * 12 + value.parkingPeriodEndMonth;

			if (endValue < startValue) {
				ctx.addIssue({
					code: 'custom',
					path: ['parkingPeriodEndMonth'],
					message: 'Parking period end must be on or after parking period start',
				});
			}
		}
	});

export type InvoiceParameters = z.infer<typeof invoiceParametersSchema>;
export type InvoiceRateTypes = z.infer<typeof invoiceRateTypeSchema>;
export type InvoiceReportFormat = z.infer<typeof invoiceReportFormatSchema>;
